import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  untracked,
  viewChild,
  HostListener,
} from '@angular/core';

import { v4 as uuid } from 'uuid';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { ModalButtons } from './interfaces';
import { ButtonTheme } from '../buttons/button/enums';
import { IconButtonTheme } from '../buttons/icon-button/enums';
import { coerceBooleanProperty } from '../../utils/utils';
import { ModalService } from './service/modal.service';

const GLOBAL_MODAL_OPEN_CLASS_NAME = 'modal-open';
const GLOBAL_IOS_SCROLL_TOUCH_CLASS_NAME = 'ios-scroll-touch';

/**
 * A modal component that displays its content in a scrollable div.
 *
 * @example
 * <b>Example with buttons from parameters</b>
 * The button is rendered inside the scrollable content
 *
 * <code>
 *   <pre>
 *    <fcom-modal
 *      [(open)]="modal.open"
 *      [title]="modal.title | finLocalization"
 *      [buttons]="ModalButtons.DONE">
 *      <div class="block pr-medium-x pr-medium-y">
 *        Some amazing content
 *      </div>
 *      <div messageBox>Place a notification above the buttons</div>
 *      <div buttons>Place custom buttons in the same place as the standard buttons</div>
 *    </fcom-modal>
 *   </pre>
 * </code>
 */
@Component({
  selector: 'fcom-modal',
  styleUrls: ['modal.component.scss'],
  templateUrl: 'modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ModalComponent implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly platform = inject(PLATFORM_ID);
  private renderer = inject(Renderer2);
  private cd = inject(ChangeDetectorRef);
  private elem = inject(ElementRef);
  protected modalService = inject(ModalService);
  private themeService = inject(FinnairThemeService);

  readonly primaryButtonTheme = computed(() =>
    this.themeService.siteTheme() === FinnairTheme.LEGACY
      ? ButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE
      : ButtonTheme.PRIMARY_ACCENT
  );

  content = viewChild<ElementRef>('content');
  modalOverlay = viewChild<ElementRef>('modalOverlay');
  modalHeading = viewChild<ElementRef>('modalHeading');

  public readonly ModalButtons = ModalButtons;
  public readonly modalId = uuid();
  public readonly ButtonTheme = ButtonTheme;
  public readonly SvgLibraryIcon = SvgLibraryIcon;
  public readonly IconButtonTheme = IconButtonTheme;

  /**
   * Boolean property indicating the state of the modal (i.e. open or closed).
   * Supports two-way data binding with [(open)].
   */
  open = model(false);

  /**
   * Stops the modal from closing until this value is changed to false. This disables
   * all buttons within the view and prevents close-button in header from rendering.
   */
  forceRemainOpen = input(false, { transform: coerceBooleanProperty });

  /**
   * The title to show in the header of the modal. If empty then the header is not rendered.
   */
  title = input<string | null>(null);

  titleClasses = input('pr-xsmall-y');

  /**
   * The buttons to show in the footer of the modal. If empty then the footer is not rendered.
   */
  buttons = input<ModalButtons | null>(ModalButtons.DONE);

  /**
   * Toggles the enabled/disabled state of the confirm button, with <code>ModalButtons.CONTINUE_CANCEL</code>
   */
  confirmEnabled = input(true);

  /**
   * Toggles the loading state of the confirm button and disables the cancel button, with <code>ModalButtons.CONTINUE_CANCEL</code>
   */
  loading = input(false);

  /**
   * Translation key to be used with the confirm button. Defaults to actions.next.
   */
  confirmTranslationKey = input('actions.next');

  /**
   * Translation key to be used with the cancel button. Defaults to actions.cancel.
   */
  cancelTranslationKey = input('actions.cancel');

  /**
   * Makes modal height to fit to content height
   */
  fitToContent = input(false);

  /**
   * Decides if overlay click event is propagated
   */
  overlayClickable = input(true);

  /**
   * Modal can take up the height of the screen
   */
  fullHeight = input(false);

  /**
   * Whether modal should be closed when continue is clicked
   */
  closeOnContinue = input(true);

  /**
   * Modal can take up the height of the screen in mobile
   */
  fullHeightMobile = input(true);

  /**
   * Extend max-width to 900px (45rem)
   */
  extraWide = input(false);

  /**
   * Add border-radius to modal content
   * Can be used to align styling if no header and footer is rendered
   */
  roundedContent = input(false);

  /**
   * ElementRef for an element that should receive focus when the modal opens
   */
  focusTarget = input<ElementRef | undefined>(undefined);

  shouldAddPaddingTop = input(false);

  isNestedModal = input(false);

  enableBrowserBackButton = input(false);

  /**
   * Called when the open state changes to closed
   */
  close = output<void>();

  /**
   * Called when modal is confirmed. Only applicable to certain buttons
   */
  confirm = output<void>();

  /**
   * Tracks whether the user attempted to close the modal while forceRemainOpen was true.
   */
  private _lastAttemptedClose = false;

  /**
   * Tracks the previous effective open state to only run DOM side effects on transitions.
   */
  private _prevOpen = false;

  constructor() {
    // Effect 1: DOM side effects — runs only on genuine open/close transitions.
    // forceRemainOpen is read untracked so changes to it don't re-trigger this effect,
    // while still letting us skip close side effects when a close is being blocked.
    // cd.detectChanges() is needed because effects run before @if re-renders the DOM,
    // so ViewChildren (content, modalHeading) would otherwise be null when setFocus() runs.
    effect(() => {
      const open = this.open();

      if (!open && untracked(() => this.forceRemainOpen())) {
        return;
      }

      if (open === this._prevOpen) {
        return;
      }
      this._prevOpen = open;

      if (open) {
        this._lastAttemptedClose = false;
        if (!this.isNestedModal()) {
          this.renderer.addClass(this.document.body, GLOBAL_MODAL_OPEN_CLASS_NAME);
        }
        this.cd.detectChanges();
        if (!this.isNestedModal()) {
          this.renderer.addClass(this.findScrollContainer(this.modalOverlay()), GLOBAL_IOS_SCROLL_TOUCH_CLASS_NAME);
        }
        this.setFocus();
        if (isPlatformBrowser(this.platform) && untracked(() => this.enableBrowserBackButton())) {
          this.pushState();
        }
      } else {
        if (!this.isNestedModal()) {
          this.renderer.removeClass(this.document.body, GLOBAL_MODAL_OPEN_CLASS_NAME);
          this.renderer.removeClass(this.findScrollContainer(this.modalOverlay()), GLOBAL_IOS_SCROLL_TOUCH_CLASS_NAME);
        }
        this.close.emit();
        if (
          isPlatformBrowser(this.platform) &&
          untracked(() => this.enableBrowserBackButton()) &&
          window.history.state?.modal
        ) {
          history.back();
        }
      }
    });

    // Effect 2: forceRemainOpen interaction — blocks close attempts and defers them
    // until forceRemainOpen clears. Only blocks a close if the modal was previously
    // open (_prevOpen), to avoid force-opening a modal that was never opened.
    effect(() => {
      const open = this.open();
      const forceRemainOpen = this.forceRemainOpen();

      if (!open && forceRemainOpen && this._prevOpen) {
        // User tried to close while blocked — remember and revert the model
        this._lastAttemptedClose = true;
        untracked(() => this.open.set(true));
      } else if (!forceRemainOpen && this._lastAttemptedClose && open) {
        // Block lifted — apply the deferred close
        this._lastAttemptedClose = false;
        untracked(() => this.open.set(false));
      }
    });
  }

  ngOnDestroy(): void {
    if (!this.isNestedModal()) {
      this.renderer.removeClass(this.document.body, GLOBAL_MODAL_OPEN_CLASS_NAME);
      this.renderer.removeClass(this.findScrollContainer(this.modalOverlay()), GLOBAL_IOS_SCROLL_TOUCH_CLASS_NAME);
    }

    if (this.open()) {
      this.close.emit();
    }
    if (this.isBrowserHistoryEnabled()) {
      history.back();
    }
  }

  private isBrowserHistoryEnabled(): boolean {
    return isPlatformBrowser(this.platform) && this.enableBrowserBackButton() && !!window.history.state?.modal;
  }

  private pushState(): void {
    const modalState = {
      modal: true,
      desc: `fcom-modal open state for "${this.title() ?? 'untitled modal'}"`,
    };
    history.pushState(modalState, null);
  }

  @HostListener('window:popstate')
  onPopstate(): void {
    if (isPlatformBrowser(this.platform) && this.enableBrowserBackButton()) {
      this.onClose();
    }
  }

  onClose(): void {
    // Guard is a fast-path: the forceRemainOpen effect would block and revert
    // open.set(false) anyway, but checking here avoids unnecessary signal churn.
    if (!this.forceRemainOpen()) {
      this.open.set(false);
    }
  }

  onCloseEvent(event: Event): void {
    event.stopPropagation();
    this.onClose();
  }

  onConfirm(): void {
    this.confirm.emit();
    if (this.closeOnContinue()) {
      this.onClose();
    }
  }

  /**
   * Only close when the overlay is clicked, and stop propagation to stop fcomClickOutside events
   * from firing on other elements.
   */
  overlayClick(event: MouseEvent): void {
    if (this.overlayClickable() && event.target === this.modalOverlay().nativeElement) {
      this.onCloseEvent(event);
    }
  }

  private setFocus(): void {
    const focusTarget = this.focusTarget();
    if (focusTarget?.nativeElement && this.elem.nativeElement.contains(focusTarget.nativeElement)) {
      focusTarget.nativeElement.focus();
    } else if (this.title()) {
      this.modalHeading().nativeElement.focus();
    } else {
      if (this.elem.nativeElement.querySelector('[data-focus-on-modal-open]')) {
        this.elem.nativeElement.querySelector('[data-focus-on-modal-open]').focus();
      } else {
        this.content().nativeElement.focus();
      }
    }
  }

  private findScrollContainer(elRef: ElementRef): HTMLElement {
    if (!elRef || !elRef.nativeElement) {
      return this.document.documentElement;
    }

    let el: HTMLElement = elRef.nativeElement;

    while (el.parentElement) {
      el = el.parentElement;
      if (el.classList.contains('scroll')) {
        return el;
      }
    }
    return this.document.documentElement;
  }
}
